import { isNil } from 'lodash';

import type {
  LineItemProps,
  ValueDescription,
} from '~/components/AiChat/SideBarDialog/InvoiceRecommendationAnalysis/types';
import { UpdateExposureLineItemsRequestCoverageDecisionOption as CoverageDecision } from '~/server_shared/openapi/data-contracts';
import toCents, { multiplyToCentsDecimal } from '~/Utils/toCents';

export const UNDECIDED = '__';
export const NO_COVERAGE = '__';
export const EXCLUDED = '__EXCLUDED__';

interface AggregateCoverage {
  id: string;
  coverage: string;
  amounts: Record<string, number>;
}

export const getAggregateCoverages = (lineItems: LineItemProps[]): AggregateCoverage[] => {
  const excludedAmounts: Record<string, number> = {};
  const coverageMap: Record<string, Record<string, number>> = {};

  lineItems.forEach((item) => {
    const coverage = item.coverage ?? NO_COVERAGE;
    const coverageDecision = item.coverage_decision ?? UNDECIDED;
    const amount = item.amount ?? 0;
    const coveredAmount = item.covered_amount ?? 0;

    if (!coverageMap[coverage]) {
      coverageMap[coverage] = {};
    }

    if (coverageDecision !== 'excluded' && !coverageMap[coverage][coverageDecision]) {
      coverageMap[coverage][coverageDecision] = 0;
    }

    if (coverageDecision === 'covered') {
      if (!coverageMap[coverage].not_covered) {
        coverageMap[coverage].not_covered = 0;
      }
      coverageMap[coverage].not_covered += amount - coveredAmount;
      coverageMap[coverage][coverageDecision] += coveredAmount;
    } else if (coverageDecision !== 'excluded') {
      coverageMap[coverage][coverageDecision] += amount;
    }

    if (coverageDecision === 'excluded') {
      excludedAmounts.covered = (excludedAmounts.covered || 0) + amount;
    }
  });

  // Cut the accumulated sums to cents: float addition of cent values leaves binary residue
  // (117.75 + 143.66 === 261.40999999999997) that would otherwise leak to callers. The
  // residue is always below half a cent, so this never moves a value off its true total.
  const quantizeAmounts = (amounts: Record<string, number>): Record<string, number> =>
    Object.fromEntries(Object.entries(amounts).map(([key, amount]) => [key, toCents(amount)]));

  // Convert the map to an array of objects
  return Object.entries(coverageMap)
    .map(([coverage, amounts]) => ({
      id: coverage,
      coverage,
      amounts: quantizeAmounts(amounts),
    }))
    .concat([
      {
        id: EXCLUDED,
        coverage: EXCLUDED,
        amounts: quantizeAmounts(excludedAmounts),
      },
    ])
    .filter((result) => Object.keys(result.amounts).length > 0);
};

export const calcCoveredAmount = (row: LineItemProps, value = row.covered_amount): number => {
  if (row.coverage_decision === 'covered' && !isNil(value)) {
    // If currency was converted, compare with base_amount, otherwise with amount
    const amountToCompare = row.base_amount && row.base_amount > 0 ? row.base_amount : row.amount;
    if (!isNil(amountToCompare)) {
      // Cut to cents: this becomes the stored covered_amount, and the frontend's world is
      // cents — every amount it sends must already be the value the user sees, even when a
      // sub-cent legacy base_amount is the cap.
      return toCents(Math.min(amountToCompare, value));
    }
  }
  return 0;
};

/**
 * Compute the auto-filled covered_amount for a line item.
 *
 * When third_party_amount is active: coverable = amount - third_party_amount
 * Otherwise: coverable = amount
 * The coverable figure is floored at 0 and, when the line was converted from a foreign
 * currency, scaled by conversion_rate — because covered_amount is denominated in the
 * policy (base) currency. The result is then capped at the `base_amount`.
 *
 * Used by both coverage_decision onChange (switching to 'covered') and
 * third_party_amount onChange (editing the value).
 */
export const computeAutoFilledCoveredAmount = (row: LineItemProps): number => {
  const amount = row.amount || 0;
  const tpa = row.third_party_amount ?? 0;
  const coverableInItemCurrency = Math.max(0, tpa > 0 ? amount - tpa : amount);
  // covered_amount lives in the policy (base) currency. Scale by conversion_rate for
  // converted lines so the foreign number is never auto-filled — and later displayed and
  // settled — as if it were a policy-currency value. The DECIMAL product is quantized to
  // cents at this source (a float product loses exact half-cents before rounding): the
  // frontend sends cents, so FE-written totals equal the sum of the displayed rows.
  const coverable = row.conversion_rate
    ? multiplyToCentsDecimal(coverableInItemCurrency, row.conversion_rate).toNumber()
    : coverableInItemCurrency;
  return calcCoveredAmount({ ...row, coverage_decision: CoverageDecision.Covered }, coverable);
};

/**
 * Re-derive covered_amount after a currency conversion is applied or re-applied to a line.
 *
 * Because covered_amount is denominated in the policy (base) currency, the *first* conversion
 * must refresh it: a coverage decision made before the conversion otherwise leaves a stale
 * foreign figure that inflates settlement and payment totals.
 *
 * On a re-apply (`wasAlreadyConverted` — the line already had a base_amount), covered_amount is
 * already policy-currency and may hold a deliberate manual partial-coverage edit, so it is left
 * untouched. Non-covered lines and depreciation-driven lines (whose covered_amount is calculated
 * separately) also keep their existing value.
 */
export const computeCoveredAmountAfterConversion = (
  row: LineItemProps,
  hasDepreciation: boolean,
  wasAlreadyConverted: boolean
): number => {
  if (wasAlreadyConverted || hasDepreciation || row.coverage_decision !== CoverageDecision.Covered) {
    return row.covered_amount ?? 0;
  }
  return computeAutoFilledCoveredAmount(row);
};

/**
 * Whether depreciation applies to a line: the exposure has depreciation enabled and the line is
 * an indemnity payable. Covered-amount auto-fill and conversion re-derivation skip these lines
 * because their covered_amount is calculated separately.
 */
export const isDepreciationApplicable = (
  row: LineItemProps,
  entityDepreciationMap: Record<string, boolean | undefined>
): boolean => {
  return Boolean(
    row.exposure_id && entityDepreciationMap[String(row.exposure_id)] === true && row.payable_type === 'indemnity'
  );
};

/**
 * Build the per-exposure depreciation flag map (exposure value -> depreciation enabled) consumed
 * by isDepreciationApplicable. Single source so column rendering and conversion re-derivation can't
 * drift out of sync.
 */
export const buildEntityDepreciationMap = (entities: ValueDescription[]): Record<string, boolean | undefined> =>
  entities.reduce<Record<string, boolean | undefined>>((acc, { value, damage_assessment_include_depreciation }) => {
    acc[value] = damage_assessment_include_depreciation;
    return acc;
  }, {});

export interface DepreciationEligibility {
  isEligible: boolean;
  reasons: string[];
}

export const isDepreciationEligible = (row: LineItemProps, policyCurrency?: string): DepreciationEligibility => {
  const hasMultiCurrency = Boolean(row.amount_currency && policyCurrency && row.amount_currency !== policyCurrency);
  const reasons: string[] = [];
  let isEligible = true;

  if (row.payable_type !== 'indemnity') {
    reasons.push('Item must be indemnity payable type');
    isEligible = false;
  }

  if (!row.category) {
    reasons.push('Category must be selected');
    isEligible = false;
  }

  if (row.coverage_decision !== 'covered') {
    reasons.push('Item must be covered');
    isEligible = false;
  }

  if (hasMultiCurrency) {
    if (!row.base_amount || row.base_amount === 0) {
      reasons.push('Missing converted amount');
      isEligible = false;
    }
  } else {
    if (!row.amount || row.amount === 0) {
      reasons.push('Amount must be greater than 0');
      isEligible = false;
    }
  }

  return { isEligible, reasons };
};
